import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  deleteAdminStandardSet,
  getAdminStandardSet,
  pollAdminStandardsJob,
  postAdminStandardSetExtract,
  postAdminStandardSetPublish,
  postAdminStandardSetUploadUrl,
  putAdminStandardSetDraft,
  type GlossaryDraftItem,
  type StandardDraftItem,
  type StandardSetDetail,
} from "../auth/api-client";
import { useAuth } from "../auth/AuthContext";

function draftFromDetail(detail: StandardSetDetail): {
  standards: StandardDraftItem[];
  glossary: GlossaryDraftItem[];
} {
  if (detail.draft) {
    return {
      standards: detail.draft.standards.map((s) => ({ ...s })),
      glossary: detail.draft.glossary.map((g) => ({ ...g })),
    };
  }
  return {
    standards: detail.published.standards.map((s) => ({
      code: s.code,
      description: s.description,
      category: s.category,
      gradeBand: s.gradeBand,
      parentCode: s.parentCode,
    })),
    glossary: detail.published.glossary.map((g) => ({ term: g.term, definition: g.definition })),
  };
}

export default function AdminStandardSetDetailPage() {
  const { setId = "" } = useParams();
  const navigate = useNavigate();
  const { getBearerToken } = useAuth();
  const bearer = getBearerToken();
  const fileRef = useRef<HTMLInputElement>(null);

  const [detail, setDetail] = useState<StandardSetDetail | null>(null);
  const [standards, setStandards] = useState<StandardDraftItem[]>([]);
  const [glossary, setGlossary] = useState<GlossaryDraftItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [phase, setPhase] = useState<"idle" | "uploading" | "extracting" | "saving" | "publishing">("idle");

  const refresh = useCallback(async () => {
    if (!bearer || !setId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getAdminStandardSet(bearer, setId);
      setDetail(data);
      const d = draftFromDetail(data);
      setStandards(d.standards);
      setGlossary(d.glossary);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not load standard set");
    } finally {
      setLoading(false);
    }
  }, [bearer, setId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function handleFile(file: File) {
    if (!bearer || phase !== "idle") return;
    if (file.type !== "application/pdf") {
      setError("Please choose a PDF file.");
      return;
    }
    setError(null);
    setNotice(null);
    try {
      setPhase("uploading");
      const { uploadUrl, key } = await postAdminStandardSetUploadUrl(bearer, setId, {
        contentType: file.type,
        sizeBytes: file.size,
      });
      const put = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": "application/pdf" }, body: file });
      if (!put.ok) throw new Error("Upload to storage failed");
      setPhase("extracting");
      const { jobId } = await postAdminStandardSetExtract(bearer, setId, { pdfKey: key });
      await pollAdminStandardsJob(bearer, jobId);
      setNotice("Extraction complete. Review the draft below, then publish.");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Extraction failed");
    } finally {
      setPhase("idle");
      if (fileRef.current) fileRef.current.value = "";
    }
  }

  async function handleSaveDraft() {
    if (!bearer || phase !== "idle") return;
    setPhase("saving");
    setError(null);
    setNotice(null);
    try {
      await putAdminStandardSetDraft(bearer, setId, { standards, glossary });
      setNotice("Draft saved.");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setPhase("idle");
    }
  }

  async function handlePublish() {
    if (!bearer || phase !== "idle") return;
    if (!window.confirm("Publish this draft? It becomes available to teachers for new lessons and courses.")) return;
    setPhase("publishing");
    setError(null);
    setNotice(null);
    try {
      await postAdminStandardSetPublish(bearer, setId, { standards, glossary });
      setNotice("Published.");
      await refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Publish failed");
    } finally {
      setPhase("idle");
    }
  }

  async function handleDelete() {
    if (!bearer || phase !== "idle") return;
    if (!window.confirm("Delete this standard set? Existing lesson links are preserved, but it is removed from new selections.")) {
      return;
    }
    try {
      await deleteAdminStandardSet(bearer, setId);
      navigate("/admin/standards");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Delete failed");
    }
  }

  function updateStandard(i: number, patch: Partial<StandardDraftItem>) {
    setStandards((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  }
  function updateGlossary(i: number, patch: Partial<GlossaryDraftItem>) {
    setGlossary((prev) => prev.map((g, idx) => (idx === i ? { ...g, ...patch } : g)));
  }

  if (loading) {
    return (
      <div className="card card-wide">
        <p className="muted">Loading…</p>
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="card card-wide">
        <div className="warn-banner" role="alert">
          {error ?? "Standard set not found."}
        </div>
        <Link className="btn secondary" to="/admin/standards">
          Back to standards
        </Link>
      </div>
    );
  }

  const busy = phase !== "idle";
  const set = detail.standardSet;

  return (
    <div className="card card-wide">
      <header className="row-header">
        <div>
          <p className="eyebrow">
            <Link to="/admin/standards">Standards</Link>
          </p>
          <h1>{set.name}</h1>
            <p className="lede">
              <span className="teacher-pill">{set.status}</span>{" "}
              {set.currentVersion ? `v${set.currentVersion} · ` : ""}
              {set.standardsCount} published standards · {set.glossaryCount} glossary terms
            </p>
          </div>
          <button type="button" className="btn ghost" disabled={busy} onClick={() => void handleDelete()}>
            Delete set
          </button>
        </header>

        {error ? (
          <div className="warn-banner" role="alert">
            {error}
          </div>
        ) : null}
        {notice ? <p className="muted">{notice}</p> : null}

        <section style={{ marginTop: "1rem" }}>
          <h2>Upload standards PDF</h2>
          <p className="muted small">
            Upload a new PDF to (re)extract. Publishing updates existing lessons&apos; available standards only; previously
            tagged lessons keep their links.
          </p>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            disabled={busy}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
            }}
          />
          {phase === "uploading" ? <p className="muted">Uploading PDF…</p> : null}
          {phase === "extracting" ? <p className="muted">AI is reading the document…</p> : null}
          {detail.document ? (
            <p className="muted small">
              Latest document: v{detail.document.version} ({detail.document.status})
              {detail.document.error ? ` — ${detail.document.error}` : ""}
            </p>
          ) : null}
        </section>

        <section style={{ marginTop: "1.5rem" }}>
          <div className="row-header">
            <h2>Standards draft ({standards.length})</h2>
            <button
              type="button"
              className="btn secondary tight"
              disabled={busy}
              onClick={() => setStandards((prev) => [...prev, { code: "", description: "", category: null, gradeBand: null }])}
            >
              Add standard
            </button>
          </div>
          {standards.length === 0 ? (
            <p className="muted">No standards yet. Upload a PDF or add rows manually.</p>
          ) : (
            <table className="teacher-table">
              <thead>
                <tr>
                  <th style={{ width: "8rem" }}>Code</th>
                  <th>Description</th>
                  <th style={{ width: "8rem" }}>Category</th>
                  <th style={{ width: "6rem" }}>Grade</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {standards.map((s, i) => (
                  <tr key={i}>
                    <td>
                      <input className="input" value={s.code} onChange={(e) => updateStandard(i, { code: e.target.value })} />
                    </td>
                    <td>
                      <textarea
                        className="input"
                        rows={2}
                        value={s.description}
                        onChange={(e) => updateStandard(i, { description: e.target.value })}
                      />
                    </td>
                    <td>
                      <input
                        className="input"
                        value={s.category ?? ""}
                        onChange={(e) => updateStandard(i, { category: e.target.value || null })}
                      />
                    </td>
                    <td>
                      <input
                        className="input"
                        value={s.gradeBand ?? ""}
                        onChange={(e) => updateStandard(i, { gradeBand: e.target.value || null })}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn ghost tight"
                        onClick={() => setStandards((prev) => prev.filter((_, idx) => idx !== i))}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section style={{ marginTop: "1.5rem" }}>
          <div className="row-header">
            <h2>Glossary draft ({glossary.length})</h2>
            <button
              type="button"
              className="btn secondary tight"
              disabled={busy}
              onClick={() => setGlossary((prev) => [...prev, { term: "", definition: "" }])}
            >
              Add term
            </button>
          </div>
          {glossary.length === 0 ? (
            <p className="muted">No glossary terms.</p>
          ) : (
            <table className="teacher-table">
              <thead>
                <tr>
                  <th style={{ width: "12rem" }}>Term</th>
                  <th>Definition</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {glossary.map((g, i) => (
                  <tr key={i}>
                    <td>
                      <input className="input" value={g.term} onChange={(e) => updateGlossary(i, { term: e.target.value })} />
                    </td>
                    <td>
                      <textarea
                        className="input"
                        rows={2}
                        value={g.definition}
                        onChange={(e) => updateGlossary(i, { definition: e.target.value })}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn ghost tight"
                        onClick={() => setGlossary((prev) => prev.filter((_, idx) => idx !== i))}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <div className="teacher-modal-actions" style={{ marginTop: "1.5rem" }}>
          <button type="button" className="btn secondary" disabled={busy} onClick={() => void handleSaveDraft()}>
            {phase === "saving" ? "Saving…" : "Save draft"}
          </button>
          <button type="button" className="btn primary" disabled={busy} onClick={() => void handlePublish()}>
            {phase === "publishing" ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>
  );
}
